import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { UserPastContribution } from "../../interface/UserDashboard.interface";
import { CalendarToday, CheckCircle, EventNote, Error, Schedule, Warning } from "@mui/icons-material";
import styles from '../../styles/scoped/user/UserHome.module.css';
import { formatAmount } from '../../utils/utilities';

interface UserPastContributionsViewProps {
    contributions: UserPastContribution[];
}

export function UserPastContributionsView({ contributions }: UserPastContributionsViewProps) {
    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'COMPLETED':
                return <CheckCircle className={`${styles.statusIcon} ${styles.completed}`} />;
            case 'PARTIAL':
                return <Warning className={`${styles.statusIcon} ${styles.partial}`} />;
            case 'OVERDUE':
                return <Error className={`${styles.statusIcon} ${styles.overdue}`} />;
            case 'MISSING':
                return <Error className={`${styles.statusIcon} ${styles.missing}`} />;
            default:
                return <Schedule className={`${styles.statusIcon} ${styles.pending}`} />;
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'COMPLETED':
                return 'Payé';
            case 'PARTIAL':
                return 'Partiel';
            case 'OVERDUE':
                return 'En retard';
            case 'MISSING':
                return 'Non payé';
            default:
                return 'En attente';
        }
    };

    if (!contributions || contributions.length === 0) {
        return (
            <div className={styles.contributionTableEmpty}>
                <CheckCircle className={styles.contributionTableEmptyIcon} />
                <p className={styles.contributionTableEmptyText}>
                    Aucun paiement effectué pour le moment
                </p>
            </div>
        );
    }

    return (
        <TableContainer
            component={Paper}
            className={`${styles.contributionTableContainer} ${styles.contributionTable}`}
        >
            <div className={styles.contributionScrollContainer}>
                <Table sx={{ minWidth: 400 }} size="small" aria-label="contributions passées">
                    <TableHead className={styles.contributionTableHead}>
                        <TableRow>
                            <TableCell
                                align="left"
                                className={styles.contributionTableHeadCell}
                            >
                                <CalendarToday sx={{ mr: 1, fontSize: '1rem' }} />
                                Période
                            </TableCell>
                            <TableCell
                                align="left"
                                className={styles.contributionTableHeadCell}
                            >
                                Montant payé
                            </TableCell>
                            <TableCell
                                align="left"
                                className={styles.contributionTableHeadCell}
                            >
                                Statut
                            </TableCell>
                            <TableCell
                                align="left"
                                className={styles.contributionTableHeadCell}
                            >
                                Date de paiement
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody className={styles.contributionTableBody}>
                        {contributions.map((contribution, index) => (
                            <TableRow
                                key={contribution.uuid}
                                className={`${styles.contributionTableRow} ${styles[`status${contribution.status.charAt(0).toUpperCase() + contribution.status.slice(1)}`]}`}
                                style={{ animationDelay: `${index * 0.1}s` }}
                            >
                                <TableCell
                                    component="th"
                                    scope="row"
                                    className={`${styles.contributionTableCell} ${styles.monthCell}`}
                                >
                                    {getStatusIcon(contribution.status)}
                                    {contribution.formattedPeriod}
                                </TableCell>
                                <TableCell
                                    component="th"
                                    scope="row"
                                    className={`${styles.contributionTableCell} ${styles.amountCell}`}
                                >
                                    <div className={styles.amountContainer}>
                                        <span className={styles.contributionAmountBadge}>
                                            {contribution.formattedAmount}
                                        </span>
                                        {contribution.status !== 'COMPLETED' && (
                                            <small className={styles.expectedAmount}>
                                                / {formatAmount(contribution.expectedAmount)}
                                            </small>
                                        )}
                                        {contribution.remainingAmount > 0 && (
                                            <small className={`${styles.difference} ${styles.deficit}`}>
                                                (reste: {formatAmount(contribution.remainingAmount)})
                                            </small>
                                        )}
                                    </div>
                                </TableCell>
                                <TableCell
                                    component="th"
                                    scope="row"
                                    className={`${styles.contributionTableCell} ${styles.statusCell}`}
                                >
                                    <span className={`${styles.statusBadge} ${styles[contribution.status.toLowerCase()]}`}>
                                        {getStatusLabel(contribution.status)}
                                    </span>
                                </TableCell>
                                <TableCell
                                    component="th"
                                    scope="row"
                                    className={`${styles.contributionTableCell} ${styles.dateCell}`}
                                >
                                    <span className={styles.paymentDate}>
                                        {contribution.formattedPaymentDate || '--'}
                                    </span>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        </TableContainer>
    );
}
